import { type IFooter, LanguageCode } from '@/interfaces';
import {
  type LocalizedField,
  type LocalizedItem,
  flattenItemList,
  localizedString,
} from '@/utils/graphqlDataModify.utils';
import { getMasterTranslationLanguageCode } from '@/utils/language.utils';
import callContentful from './base.fetchers';

type GetFooter = (args: {
  isPreviewMode: boolean;
  languageCode?: LanguageCode;
  isMultiLanguagePage: boolean;
  slug: string;
}) => Promise<IFooter[]>;

const flattenPageLinks = (items: Array<LocalizedItem> | null, lang: string) =>
  items?.map((item) => ({
    ...item,
    title: localizedString(item.title as LocalizedField, lang),
  }));

export const getFooter: GetFooter = async ({
  isPreviewMode,
  languageCode = LanguageCode.English,
  isMultiLanguagePage = false,
  slug,
}) => {
  const languageField = getMasterTranslationLanguageCode(languageCode);

  const query = `
    {
      footerCollection(
        preview: ${isPreviewMode}
        where: { forPmtPage: ${isMultiLanguagePage} }
        limit: 10
      ) {
        items {
          subText {
            ${languageField}
          }
          itemList
          pageLinks
          copyright {
            ${languageField}
          }
          ppText {
            ${languageField}
          }
          appBadgeMainText {
            ${languageField}
          }
          appBadgeSubText {
            ${languageField}
          }
          appStoreUrl
          googlePlayUrl
          appBadgeCountry
        }
      }
    }
  `;

  const response = await callContentful(query, isPreviewMode, slug, 'footer.fetchers.getFooter');

  const rawFooterData: LocalizedItem[] = Array.isArray(response?.data?.footerCollection?.items)
    ? (response.data.footerCollection.items as LocalizedItem[]).filter(Boolean)
    : [];

  const footerData = rawFooterData.map(
    (footer: LocalizedItem): IFooter =>
      ({
        ...footer,
        subText: localizedString(footer.subText as LocalizedField, languageField),
        copyright: localizedString(footer.copyright as LocalizedField, languageField),
        ppText: localizedString(footer.ppText as LocalizedField, languageField),
        appBadgeMainText: localizedString(footer.appBadgeMainText as LocalizedField, languageField),
        appBadgeSubText: localizedString(footer.appBadgeSubText as LocalizedField, languageField),
        itemList: flattenItemList(footer.itemList as Array<LocalizedItem> | null, languageField),
        pageLinks: flattenPageLinks(footer.pageLinks as Array<LocalizedItem> | null, languageField),
      }) as IFooter,
  );

  return footerData;
};
